'use client'

import { captureException } from '@sentry/nextjs'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { checkErrorInstance } from 'utils/check-error-instance'
import getEntitiesBySlugs from 'utils/get-all-entities-slugs'
import { ls } from 'utils/local-storage'
import type {
  ComparisonCartCards,
  LocalStorageComparisonCart,
} from './comparisonCartCardsContext'
import { logger } from 'utils/logger'

const COMPARISON_CART_FROM_CATEGORY_BANNER_VERSION = '1.0' as const

const COMPARISON_CART_FROM_CATEGORY_BANNER_LOCAL_STORAGE_KEY =
  'comparisonCartCategoryBanner' as const

const checkAndCaptureError = (originalError: unknown): void => {
  const error = checkErrorInstance(originalError)

  captureException(error)
}

type UpdateComparisonCartFromCategoryBannerCards = (
  action: 'add' | 'reset',
  cards?: ComparisonCartCards,
) => void

type ComparisonCartFromCategoryBannerCardsContextValue = {
  comparisonCartFromCategoryBannerCards: ComparisonCartCards
  updateComparisonCartFromCategoryBannerCards: UpdateComparisonCartFromCategoryBannerCards
}

const ComparisonCartFromCategoryBannerCardsContext =
  createContext<ComparisonCartFromCategoryBannerCardsContextValue>({
    comparisonCartFromCategoryBannerCards: [],
    updateComparisonCartFromCategoryBannerCards: () => null,
  })

/**
 * @todo: A lot of the same improvements could be made to this as could be
 * made to ComparisonCartCardsProvider. Additionally, the architecture that
 * necessitates having a separate set of cards for the comparison cart when
 * triggered from a category page banner could likely be improved such that
 * this was not necessary.
 *
 * @see ComparisonCartCardsProvider
 */
export const ComparisonCartFromCategoryBannerCardsProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [
    comparisonCartFromCategoryBannerCards,
    setComparisonCartFromCategoryBannerCards,
  ] = useState<ComparisonCartCards>([])

  const updateComparisonCartFromCategoryBannerCards: UpdateComparisonCartFromCategoryBannerCards =
    useCallback(
      (action, cards) => {
        try {
          const getCardSlugsFromCards = (
            cardsToGetSlugsFrom?: ComparisonCartCards,
          ) => (cardsToGetSlugsFrom || []).map((card) => card.slug)

          const comparisonCartFromCategoryBannerCardSlugs =
            getCardSlugsFromCards(comparisonCartFromCategoryBannerCards)

          let filteredCards: ComparisonCartCards

          switch (action) {
            case 'add':
              filteredCards = (cards || []).filter(
                (card) =>
                  !comparisonCartFromCategoryBannerCardSlugs.includes(
                    card.slug,
                  ),
              )
              break
            case 'reset':
              filteredCards = comparisonCartFromCategoryBannerCards
              break
            default:
              filteredCards = []
          }

          switch (action) {
            case 'add':
              setComparisonCartFromCategoryBannerCards([
                ...comparisonCartFromCategoryBannerCards,
                ...filteredCards,
              ])
              break
            case 'reset':
              setComparisonCartFromCategoryBannerCards([])
              break
            default:
              return
          }
        } catch (error) {
          checkAndCaptureError(error)
        }
      },
      [comparisonCartFromCategoryBannerCards],
    )

  useEffect(() => {
    const getCardSlugsFromLocalStorageAndUpdateComponentState = async () => {
      try {
        const lsComparisonCartFromCategoryBanner: LocalStorageComparisonCart | null =
          ls.getItem(
            COMPARISON_CART_FROM_CATEGORY_BANNER_LOCAL_STORAGE_KEY,
            ['version'],
            COMPARISON_CART_FROM_CATEGORY_BANNER_VERSION,
          )

        if (
          lsComparisonCartFromCategoryBanner &&
          lsComparisonCartFromCategoryBanner.cardIds.length > 0
        ) {
          const entities = await getEntitiesBySlugs(
            lsComparisonCartFromCategoryBanner.cardIds,
          )

          if (entities && !entities.error) {
            setComparisonCartFromCategoryBannerCards(entities.cards)
          } else {
            logger.error(
              'Error getting entities for ComparisonCartFromCategoryBannerCardsProvider',
            )
          }
        }
      } catch (error) {
        checkAndCaptureError(error)
      }
    }

    getCardSlugsFromLocalStorageAndUpdateComponentState()
  }, [])

  useEffect(() => {
    const lsComparisonCartFromCategoryBanner: LocalStorageComparisonCart = {
      version: COMPARISON_CART_FROM_CATEGORY_BANNER_VERSION,
      cardIds: comparisonCartFromCategoryBannerCards.map((card) => card.slug),
    }
    try {
      ls.setItem(
        COMPARISON_CART_FROM_CATEGORY_BANNER_LOCAL_STORAGE_KEY,
        lsComparisonCartFromCategoryBanner,
      )
    } catch (error) {
      checkAndCaptureError(error)
    }
  }, [comparisonCartFromCategoryBannerCards])

  return (
    <ComparisonCartFromCategoryBannerCardsContext.Provider
      value={{
        comparisonCartFromCategoryBannerCards,
        updateComparisonCartFromCategoryBannerCards,
      }}
    >
      {children}
    </ComparisonCartFromCategoryBannerCardsContext.Provider>
  )
}

export const useComparisonCartFromCategoryBannerCards = () =>
  useContext(ComparisonCartFromCategoryBannerCardsContext)
